import isEmpty from "lodash/isEmpty";
import {collection, Firestore, getDocs, query, QueryConstraint} from "firebase/firestore";
import {RemovableRef} from "@vueuse/core";
import {Configuration, Product} from "@/lib/keys";

export const tap = <T>(value: T, cb: (value: T) => undefined | T): undefined | T => {
  return cb(value);
}

export const sortByColumn = (items: Record<string, any>[], query: string, column: string = 'name') => {
  if (isEmpty(query)) {
    return items;
  }
  return items.filter((b) => b[column].toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => a[column].toLowerCase().indexOf(query.toLowerCase()) - b[column].toLowerCase().indexOf(query.toLowerCase()));
}

export const loadBucketCollectionItemBy = async <T = object[] | object>(collectionPath?: string, slug?: string) => {
  const url = `https://storage.googleapis.com/snuspris-no.appspot.com${collectionPath && slug ? `/${collectionPath}` : ''}/${slug ? slug : collectionPath}.json`
  return await fetch(url).then(res => res.json()) as T;
}

export const loadBucketCollectionToRef = <T = object[] | object>(writeRef: RemovableRef<T[] | T>, collectionPath: string, slug?: string) => {
  return async () => {
    const response = await loadBucketCollectionItemBy<T>(collectionPath, slug);
    writeRef.value = Array.isArray(response) ? response as T[] : response as T;
  }
}

export const loadBucketItemToRef = <T = object>(writeRef: RemovableRef<T>, collectionPath: string, slug: string) => {
  return loadBucketCollectionToRef(writeRef, collectionPath, slug)
}

export const loadCollectionToRef = <T = object[]>(firestore: Firestore, writeRef: RemovableRef<T[]>, collectionPath: string, filters = []) => {
  return loadCollectionToRefWithFilter<T>(firestore, writeRef, collectionPath, filters)
}

// export const loadEncryptedBlobToRef = (url: string, writeRef: Ref<object[]>, collectionPath: string) => {
//     return async () =>{
//
//     }
// }
type FilterFunction = (item: object) => boolean;

interface CollectionRefConfig {
  filterFn: FilterFunction | null;
}

export const loadCollectionToRefWithFilter = <T = object[]>(
  firestore: Firestore,
  writeRef: RemovableRef<T[]>,
  collectionPath: string,
  filters: QueryConstraint[] = [],
  config: CollectionRefConfig = {filterFn: null},
) => {
  return async () => {
    if (writeRef.value.length > 0) {
      return;
    }

    const q = query(
      collection(firestore, collectionPath),
      ...filters,
    );

    const docs = await getDocs(q);

    if (config.filterFn) {
      writeRef.value = docs.docs.filter(config.filterFn).map((doc) => {
        const data = doc.data();
        return {
          ...data,
        }
      }) as T[];
    } else {
      writeRef.value = docs.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
        }
      }) as T[];
    }
  }
}

export const loadDocumentsBy = async <T = object>(firestore: Firestore, collectionPath: string, filters: QueryConstraint[] = []): Promise<T[]> => {
  const snapshot = query(
    collection(firestore, collectionPath),
    ...filters
  );

  const document = await getDocs(snapshot);

  if (!document || !document.docs || !document.docs.length) {
    return [];
  }

  return document.docs.map(doc => {
    const data = doc.data();
    return {
      ...data
    } as T;
  })
}

const extractHeadingSizeFromString = (input: string): number => {
  const regex = /h(\d+)/;
  const matches = input.replace(' ', '').match(regex);
  return Math.round(parseInt(matches ? matches[1] : '5'));
}

export const shrinkOrGrowHeading = (input: string, amount: number, subtract = true) => {

  let number = extractHeadingSizeFromString(input);

  if (number < 1 || number > 6) {
    number = 3;
  }
  return `h${subtract ? number - amount : number + amount}`
}

export const scaleHeading = (input: string, scale: number): string => {
  let number = extractHeadingSizeFromString(input);

  if (number < 1 || number > 6) {
    number = 3;
  }

  return `h${Math.round(number * scale)}`;
}

export const getLowestPrice = (product: Product): Configuration => {
  const sorted = product.prices
    .sort((a, b) => a.price > b.price ? -1 : 1);

  if (sorted.length === 0) {
    return {
      price: 1,
      merchant_id: 0,
      in_stock: true,
      merchant: product.merchants[0],
      detail_url: ''
    };
  }


  return sorted[0] as Configuration;
}

export const isCurrentHost = (hosts: string | string[] | null = null): boolean | string => {
  let currentHost: string = document.location.host ?? 'localhost';

  if (currentHost === 'localhost:8080' || /:\d{1,10}/.test(currentHost)) {
    currentHost = currentHost.replace(/:\d{1,10}/, '');
  }

  if (!hosts) {
    return currentHost;
  }

  if (Array.isArray(hosts) && hosts.includes(currentHost)) {
    return true;
  }

  return hosts === currentHost;
};


export const generateCUID = () => {
  const timestamp = new Date().getTime().toString(36).slice(-6).toUpperCase();
  const randomPart = Math.random().toString(36).slice(-4).toUpperCase();

  return `${timestamp}${randomPart}`;
}

export const fancyJoin = (
  items: any[],
  separator = ', ',
  lastSeparator: string | undefined = undefined,
): string => {
  if (items.length === 1) {
    return items.shift();
  }

  if (items.length === 2) {
    return items.join(lastSeparator ?? separator).trim();
  }

  const lastItem = items.pop();

  return items.join(separator).concat(lastSeparator ?? separator, lastItem).trim()
}
